import { memo, useRef } from "react"

import { getPrefixedClassNames, styles } from "./styles"

import { Area } from "./types"
import { cx } from "@emotion/css"
import { useFocusManagement } from "./FocusManager"
import { useInViewport } from "react-in-viewport"
import { useTableContext } from "./context"

type GridCellProps = {
  rowIndex: number
  rowId: string
  columnId: string
  area: Area
  editDisabled?: boolean
  cellIndex?: number
}

export const GridCell = memo(function _GridCell(props: GridCellProps) {
  const {
    rowIndex,
    rowId,
    columnId,
    area,
    editDisabled = false,
    cellIndex,
  } = props
  // viewport check
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const { inViewport } = useInViewport(viewportRef, undefined, {
    disconnectOnLeave: false,
  })

  const {
    columnManager: cm,
    getCellProps,
    disabled,
    scrollingFast,
  } = useTableContext()
  const column = cm.getColumnById(columnId)
  const columnIndex = cm.getColumnIndexById(columnId)

  const { getManagerProps } = useFocusManagement()

  if (!column) return null

  const { width, align = "left", dataKey, Cell, ...rest } = column
  const { ref, ...managerProps } = getManagerProps(rowIndex, columnIndex)
  const { className, text = " - " } = getCellProps(
    rowId,
    dataKey,
    rowIndex,
    columnId,
  )

  if (scrollingFast && area !== "fixed") {
    return (
      <div
        role="gridcell"
        data-grid-row-cell
        className={cx(
          styles.cell,
          styles.bodyCell,
          styles.aligns[align],
          getPrefixedClassNames("cell", "grid-cell"),
          className,
        )}
        style={{ width }}
      />
    )
  }

  return (
    <div
      id={`grid-cell-${cellIndex! + 1}`}
      role="gridcell"
      data-grid-row-cell
      data-column-id={columnId}
      {...managerProps}
      ref={(el) => {
        ref(el)
        viewportRef.current = el
      }}
      className={cx(
        styles.cell,
        styles.bodyCell,
        styles.aligns[align],
        getPrefixedClassNames("cell", "grid-cell"),
        className,
      )}
      style={{ width }}
    >
      {inViewport || area === "fixed" ? (
        Cell ? (
          <Cell
            rowIndex={rowIndex}
            rowId={rowId}
            columnIndex={columnIndex}
            columnId={columnId}
            dataKey={dataKey}
            align={align}
            disabled={disabled || editDisabled}
            {...rest}
          />
        ) : (
          <span className={styles.cellText}>{text}</span>
        )
      ) : (
        <div>...</div>
      )}
    </div>
  )
})
